import { useEffect, useState, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Shirt, ChevronUp, ChevronDown } from "lucide-react";
import { EnlightenmentContext } from "@/context/EnlightenmentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GarmentCollection = () => {
  const { sessionId, currentTier } = useContext(EnlightenmentContext);
  const [garments, setGarments] = useState([]);
  const [allGarments, setAllGarments] = useState({});
  const [expanded, setExpanded] = useState(null); // {tier, ...garment}
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('wardrobeOpen') === 'true'; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('wardrobeOpen', open ? 'true' : 'false'); } catch { /* ignore */ }
  }, [open]);

  useEffect(() => {
    const fetchGarments = async () => {
      try {
        const [userRes, allRes] = await Promise.all([
          fetch(`${API}/session/${sessionId}/garments`),
          fetch(`${API}/garments/all`)
        ]);
        const userData = await userRes.json();
        const allData = await allRes.json();
        setGarments(userData.garments || []);
        setAllGarments(allData.garments || {});
      } catch (err) { /* garments non-critical */ }
    };
    fetchGarments();
  }, [sessionId, currentTier]);

  const unlockedTiers = new Set(garments.map(g => g.tier));
  const totalCount = Object.keys(allGarments).length;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl mb-6 overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1A1030 0%, #2D1F50 60%, #1A2810 100%)',
          boxShadow: '0 8px 32px rgba(26, 16, 48, 0.25)'
        }}
        data-testid="garment-collection"
      >
        {/* Wardrobe header — always visible, click to toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-3 p-4 transition hover:bg-white/5"
          data-testid="wardrobe-toggle"
          aria-expanded={open}
        >
          {/* Wardrobe icon */}
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 relative"
            style={{
              background: 'linear-gradient(135deg, #6a4a26 0%, #3e2b14 100%)',
              boxShadow: 'inset 0 0 8px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.4)',
              border: '1px solid #2a1a08'
            }}
            aria-hidden="true"
          >
            <Shirt size={20} style={{ color: '#D4A574' }} />
            {/* Tiny knob */}
            <div className="absolute right-1 top-1/2 -translate-y-1/2 w-1 h-1 rounded-full" style={{ background: '#D4A574', boxShadow: '0 0 3px #D4A574' }} />
          </div>
          <div className="flex-1 text-left">
            <div className="text-xs font-bold uppercase tracking-widest" style={{ color: '#D4A574', fontFamily: "'Cinzel', serif" }}>
              Wardrobe of Sacred Garments
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: '#B8A8D8', fontFamily: "'Nunito', sans-serif" }}>
              {garments.length} of {totalCount} unlocked
              <span className="mx-1">&middot;</span>
              {open ? 'tap to close' : 'tap to open'}
            </div>
          </div>
          <div className="flex-shrink-0 px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1"
            style={{ background: 'rgba(212,165,116,0.15)', color: '#D4A574' }}>
            {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            {open ? 'Close' : 'Open'}
          </div>
        </button>

        {/* Garment grid — collapsible */}
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="wardrobe-grid"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="px-5 pb-5"
            >
              <p className="text-[10px] mb-3 italic" style={{ color: '#B8A8D8', fontFamily: "'Nunito', sans-serif" }}>
                Click a garment to expand and read its full attributes
              </p>

              {(() => {
                // Split garments into 3 sections so the Noctyra/Lv28 and Lv29 outfits are visually grouped.
                const entries = Object.entries(allGarments).map(([k, v]) => [parseInt(k), v]);
                const regular = entries.filter(([t]) => t < 27).sort((a, b) => a[0] - b[0]);
                const holySoldier = entries.filter(([t]) => t >= 27 && t <= 31).sort((a, b) => a[0] - b[0]);
                const primordeiaArmor = entries.filter(([t]) => t >= 32 && t <= 36).sort((a, b) => a[0] - b[0]);

                const renderTile = ([tier, garment]) => {
                  const isUnlocked = unlockedTiers.has(tier);
                  return (
                    <motion.button
                      key={tier}
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.96 }}
                      onClick={() => setExpanded({ tier, ...garment, unlocked: isUnlocked })}
                      className="rounded-xl p-2 text-center relative transition cursor-pointer"
                      style={{
                        backgroundColor: isUnlocked ? 'rgba(212,165,116,0.15)' : 'rgba(139,125,168,0.08)',
                        border: isUnlocked ? '1px solid rgba(212,165,116,0.3)' : '1px solid rgba(139,125,168,0.15)',
                        opacity: isUnlocked ? 1 : 0.55
                      }}
                      data-testid={`garment-tile-${tier}`}
                      aria-label={`${garment.name} — ${isUnlocked ? 'unlocked' : 'locked'}`}
                    >
                      <div className="text-2xl mb-1" style={{ filter: isUnlocked ? 'none' : 'grayscale(1) blur(0.3px)' }}>
                        {garment.emoji}
                      </div>
                      <div className="text-[9px] font-bold leading-tight" style={{ color: isUnlocked ? '#D4A574' : '#8B7DA8' }}>
                        {garment.name}
                      </div>
                      {!isUnlocked && (
                        <div className="absolute top-1 right-1">
                          <Lock size={8} style={{ color: '#8B7DA8' }} />
                        </div>
                      )}
                      {isUnlocked && (
                        <div className="text-[8px] mt-0.5 leading-tight" style={{ color: '#B8A8D8' }}>
                          Tier {tier + 1}
                        </div>
                      )}
                    </motion.button>
                  );
                };

                const SectionHeader = ({ label, sublabel, count, testId }) => (
                  <div className="flex items-baseline justify-between mt-4 mb-2" data-testid={testId}>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.18em]"
                        style={{ color: '#D4A574', fontFamily: "'Cinzel', serif" }}>
                        {label}
                      </div>
                      {sublabel && (
                        <div className="text-[9px] italic" style={{ color: '#B8A8D8' }}>{sublabel}</div>
                      )}
                    </div>
                    <div className="text-[9px] font-semibold" style={{ color: '#B8A8D8' }}>
                      {count}/5
                    </div>
                  </div>
                );

                const sectionStyle = (highlight) => ({
                  background: highlight
                    ? 'linear-gradient(135deg, rgba(212,165,116,0.08), rgba(123,94,167,0.06))'
                    : 'transparent',
                  border: highlight ? '1px solid rgba(212,165,116,0.25)' : 'none',
                  borderRadius: 16,
                  padding: highlight ? 10 : 0,
                });

                const holySoldierCount = holySoldier.filter(([t]) => unlockedTiers.has(t)).length;
                const armorCount = primordeiaArmor.filter(([t]) => unlockedTiers.has(t)).length;
                const onHolySoldier = currentTier >= 27 && currentTier <= 31;
                const onArmor = currentTier >= 32;

                return (
                  <>
                    {/* Regular garments — one per tier (Tiers 1-27) */}
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="garments-regular-grid">
                      {regular.map(renderTile)}
                    </div>

                    {/* Outfit of the Holy Soldier — Lv28 (Tiers 28-32) */}
                    {holySoldier.length > 0 && (
                      <div style={sectionStyle(onHolySoldier)} data-testid="outfit-holy-soldier">
                        <SectionHeader
                          label="\u2728 Outfit of the Holy Soldier"
                          sublabel="Level 28 — Noctyra · 5 pieces"
                          count={holySoldierCount}
                          testId="outfit-holy-soldier-header"
                        />
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {holySoldier.map(renderTile)}
                        </div>
                      </div>
                    )}

                    {/* Armor of Primordeia — Lv29 (Tiers 33-34) */}
                    {primordeiaArmor.length > 0 && (
                      <div style={sectionStyle(onArmor)} data-testid="outfit-primordeia-armor">
                        <SectionHeader
                          label="\u2728 Armor of Primordeia"
                          sublabel="Level 29 · 5 pieces"
                          count={armorCount}
                          testId="outfit-primordeia-armor-header"
                        />
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {primordeiaArmor.map(renderTile)}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Expanded Garment Modal */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpanded(null)}
            className="fixed inset-0 z-[350] flex items-center justify-center px-4"
            style={{ background: 'rgba(14, 8, 24, 0.82)', backdropFilter: 'blur(10px)' }}
            data-testid="garment-expanded-modal"
          >
            <motion.div
              initial={{ scale: 0.85, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 18, stiffness: 220 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-sm w-full rounded-3xl p-8 text-center"
              style={{
                background: 'linear-gradient(135deg, #1A1030 0%, #2D1F50 50%, #1A2810 100%)',
                boxShadow: '0 40px 120px rgba(0, 0, 0, 0.7), 0 0 80px rgba(212,165,116,0.3)',
                border: '1px solid rgba(212,165,116,0.4)'
              }}
            >
              <button
                onClick={() => setExpanded(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center hover:bg-white/10 transition"
                style={{ color: '#D4A574' }}
                data-testid="close-garment-modal"
                aria-label="Close"
              >
                <X size={18} />
              </button>

              {/* Shimmer ring around the emoji */}
              <div className="flex justify-center mb-4">
                <motion.div
                  animate={expanded.unlocked ? {
                    boxShadow: [
                      '0 0 40px rgba(212,165,116,0.6)',
                      '0 0 70px rgba(212,165,116,0.95)',
                      '0 0 40px rgba(212,165,116,0.6)'
                    ]
                  } : {}}
                  transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
                  className="relative w-28 h-28 rounded-full flex items-center justify-center"
                  style={{
                    background: expanded.unlocked
                      ? 'radial-gradient(circle, rgba(212,165,116,0.25) 0%, rgba(79,55,139,0.25) 100%)'
                      : 'radial-gradient(circle, rgba(139,125,168,0.15), rgba(79,55,139,0.15))',
                    border: '2px solid rgba(212,165,116,0.5)'
                  }}
                >
                  <span
                    className="text-6xl"
                    style={{ filter: expanded.unlocked ? 'none' : 'grayscale(0.7) blur(1px)' }}
                  >
                    {expanded.emoji}
                  </span>
                </motion.div>
              </div>

              <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: '#D4A574', fontFamily: "'Cinzel', serif" }}>
                Tier {expanded.tier + 1} Garment
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: '#FFF', fontFamily: "'Fredoka', sans-serif" }} data-testid="expanded-garment-name">
                {expanded.name}
              </h3>

              <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(212,165,116,0.2)' }}>
                <div className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: '#D4A574' }}>
                  Attribute
                </div>
                <p className="text-sm leading-relaxed italic" style={{ color: '#E8E0F8', fontFamily: "'Nunito', sans-serif" }}>
                  &ldquo;{expanded.desc}&rdquo;
                </p>
              </div>

              <div className="text-xs font-semibold" style={{ color: expanded.unlocked ? '#4CAF50' : '#8B7DA8', fontFamily: "'Fredoka', sans-serif" }}>
                {expanded.unlocked ? '\u2728 Unlocked — you carry this now' : '\u{1F512} Locked — continue the journey to earn it'}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default GarmentCollection;
